import { IcColorStatusSActive } from '@assets/icons/24x24';
import { Box, For, HStack, Icon, Tabs, Text, VStack } from '@chakra-ui/react';
import { ScrollArea } from '@components/ui/ScrollArea';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { SensorOrSubSensorInfoByType, SensorOrSubSensorTypeEnum } from '../../monitoring.types';
import { statusColorFromSensorStatus } from '../../monitoring.utils';
import { SensorSection } from './GlobalTab';

function formatName(name: string) {
  if (name.includes('.')) {
    return <FormattedMessage id={`components.monitoring.sensorDetail.global.${name}`} defaultMessage={name} />;
  }
  return name;
}

type Props<T extends SensorOrSubSensorTypeEnum> = {
  sections: [string, SensorSection][];
  sensorOrSubSensorInfo: SensorOrSubSensorInfoByType<T>;
};

export function SectionViewer<T extends SensorOrSubSensorTypeEnum>({
  sections,
  sensorOrSubSensorInfo,
}: Readonly<Props<T>>) {
  const [activeSection, setActiveSection] = useState<string>(sections.map(([key]) => key)[0]);

  const activeSectionRef = useRef(activeSection);
  const lockedRef = useRef(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const lastSectionRef = useRef<HTMLDivElement | null>(null);
  const [lastSectionHeight, setLastSectionHeight] = useState(0);

  const handleClick = (id: string) => {
    const element = sectionRefs.current[id];
    const container = scrollContainerRef.current;
    if (!element || !container) return;

    setActiveSection(id);
    lockedRef.current = true;

    const targetTop = element.offsetTop;
    container.scrollTo({ top: targetTop, behavior: 'smooth' });

    const checkScroll = () => {
      if (Math.abs(container.scrollTop - targetTop) < 1) {
        lockedRef.current = false;
      } else {
        requestAnimationFrame(checkScroll);
      }
    };
    requestAnimationFrame(checkScroll);
  };

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleUserScroll = () => {
      if (lockedRef.current) lockedRef.current = false;
    };

    container.addEventListener('wheel', handleUserScroll, { passive: true });
    container.addEventListener('touchmove', handleUserScroll, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleUserScroll);
      container.removeEventListener('touchmove', handleUserScroll);
    };
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      if (lockedRef.current) return;

      const scrollTop = container.scrollTop;
      const sorted = Object.entries(sectionRefs.current)
        .map(([id, element]) => ({ id, top: element?.offsetTop ?? 0 }))
        .sort((a, b) => a.top - b.top);

      const current = sorted.findLast((section) => scrollTop >= section.top)?.id ?? sorted[0]?.id;

      if (current && current !== activeSectionRef.current) {
        activeSectionRef.current = current;
        setActiveSection(current);
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [sections]);

  // Mesure last section height
  useEffect(() => {
    const el = lastSectionRef.current;
    if (!el) return;

    const measure = () => {
      setLastSectionHeight(el.offsetHeight);
    };

    measure();

    const obs = new ResizeObserver(measure);
    obs.observe(el);

    return () => obs.disconnect();
  }, []);

  const lastId = sections.at(-1)?.[0];

  return (
    <VStack gap={0.25} width="100%" height="100%">
      {sensorOrSubSensorInfo.status !== null && (
        <HStack flexShrink={0} gap={7} paddingY={3} width="100%" backgroundColor="neutral.700" justifyContent="center">
          {'connectorStatus' in sensorOrSubSensorInfo.status && (
            <HStack gap={2}>
              <Icon
                boxSize="24px"
                color={statusColorFromSensorStatus[sensorOrSubSensorInfo.status.connectorStatus]}
                as={IcColorStatusSActive}
              />
              <Text fontSize="md">
                <FormattedMessage id={`sensors.status.${sensorOrSubSensorInfo.status.connectorStatus}`} />
              </Text>
            </HStack>
          )}
          {'dataAvailability' in sensorOrSubSensorInfo.status && (
            <HStack gap={2}>
              <Icon
                boxSize="24px"
                color={statusColorFromSensorStatus[sensorOrSubSensorInfo.status.dataAvailability]}
                as={IcColorStatusSActive}
              />
              <Text fontSize="md">
                <FormattedMessage id={`sensors.status.${sensorOrSubSensorInfo.status.dataAvailability}`} />
              </Text>
            </HStack>
          )}
          <HStack gap={2}>
            <Icon
              boxSize="24px"
              color={statusColorFromSensorStatus[sensorOrSubSensorInfo.status.healthStatus]}
              as={IcColorStatusSActive}
            />
            <Text fontSize="md">
              <FormattedMessage id={`sensors.status.${sensorOrSubSensorInfo.status.healthStatus}`} />
            </Text>
          </HStack>
        </HStack>
      )}
      <Tabs.Root
        orientation="vertical"
        size="md"
        lazyMount
        variant="vertical"
        userSelect="none"
        flex={1}
        value={activeSection}
        onValueChange={(e) => handleClick(e.value)}
      >
        <ScrollArea backgroundColor="neutral.800" width="335px" flexShrink={0}>
          {sections.length > 0 && (
            <Tabs.List width="100%">
              <For each={sections}>
                {([name, [, color]]) => (
                  <Tabs.Trigger key={name} value={name}>
                    <HStack width="100%">
                      <Text>{formatName(name)}</Text>
                      {color !== null && (
                        <Icon marginLeft="auto" boxSize="24px" color={color} as={IcColorStatusSActive} />
                      )}
                    </HStack>
                  </Tabs.Trigger>
                )}
              </For>
            </Tabs.List>
          )}
        </ScrollArea>
        <ScrollArea contentProps={{ height: '100%' }} backgroundColor="neutral.800" viewportRef={scrollContainerRef}>
          <VStack width="100%" height="100%" gap={0.25}>
            <For each={sections}>
              {([name, [Component, color]]) => (
                <VStack
                  gap={0.25}
                  width="100%"
                  key={name}
                  id={name}
                  ref={(el) => {
                    sectionRefs.current[name] = el;
                    if (name === lastId) lastSectionRef.current = el;
                  }}
                >
                  <HStack backgroundColor="neutral.700" width="100%" height="64px" paddingLeft={5} gap={1}>
                    <Text paddingY="auto" fontWeight="medium">
                      {formatName(name)}
                    </Text>
                    {color !== null && <Icon boxSize="24px" color={color} as={IcColorStatusSActive} />}
                  </HStack>
                  {Component}
                </VStack>
              )}
            </For>
            <Box
              width="100%"
              flex="0 0 auto"
              minHeight={`calc(100% - ${lastSectionHeight}px - 2px)`}
              backgroundColor="neutral.700"
            />
          </VStack>
        </ScrollArea>
      </Tabs.Root>
    </VStack>
  );
}
